// Ad-click attribution capture for the Conversions API.
//
// When a visitor arrives from an ad, the platform appends a click id to the URL
// (`?fbclid=`, `?ttclid=`, `?twclid=`, `?gclid=`). We capture it on first touch
// into localStorage so it survives navigation to the waitlist form, then
// forward it with the signup so the server can attribute the conversion to the
// click. These ids cannot be recovered after the fact — capturing them now is
// the load-bearing prep for turning on ads later.
//
// Consent: storing a click id is device storage for a marketing purpose, so in
// the EU/EEA/UK it needs the visitor's prior permission (ePrivacy Art. 5(3)),
// and forwarding it with a signup makes it personal data under GDPR. There the
// id is read from the URL into memory only — a JS variable is not device
// storage — and is written out solely if the visitor allows it. Until then
// `getClickIds()` returns nothing, so an unconsented id is never egressed
// either. Everywhere else the id is stored immediately, as before.

const CLICK_ID_KEYS = ["fbclid", "ttclid", "twclid", "gclid"] as const;
type ClickIdKey = (typeof CLICK_ID_KEYS)[number];

export type ClickIds = Partial<Record<ClickIdKey, string>>;

const STORAGE_KEY = "mavori_click_ids";
const CONSENT_KEY = "mavori_attribution_consent";
const MAX_LEN = 512;

export type AttributionConsent = "granted" | "denied";

/** Held in memory only, pending a consent decision. Never persisted from here. */
let pending: ClickIds = {};

function readStored(): ClickIds {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: ClickIds = {};
    for (const key of CLICK_ID_KEYS) {
      const value = parsed[key];
      if (typeof value === "string" && value) out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

/** Click ids present in the current URL. Pure read — touches no storage. */
export function readClickIdsFromUrl(): ClickIds {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const out: ClickIds = {};
    for (const key of CLICK_ID_KEYS) {
      const value = params.get(key);
      if (value) out[key] = value.slice(0, MAX_LEN);
    }
    return out;
  } catch {
    return {};
  }
}

function persist(ids: ClickIds): void {
  if (typeof window === "undefined") return;
  const incoming = Object.keys(ids);
  if (incoming.length === 0) return;
  try {
    const stored = readStored();
    let changed = false;
    for (const key of CLICK_ID_KEYS) {
      const value = ids[key];
      // First touch wins: the ad that originally brought the visitor is the one
      // the conversion should be attributed to.
      if (value && !stored[key]) {
        stored[key] = value;
        changed = true;
      }
    }
    if (changed) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Private-mode / disabled storage — attribution is best-effort.
  }
}

/**
 * Capture any click ids from the current URL into first-touch localStorage.
 * Call once on app load in regions that do not require prior consent.
 */
export function captureClickIdsFromUrl(): void {
  persist(readClickIdsFromUrl());
}

/**
 * Read click ids from the URL into memory without storing them. Call once on
 * app load in a consent-required region, before the router can rewrite the
 * query string. Returns whether there was anything to hold, which is what
 * decides if the visitor is asked at all — no ad click, no prompt.
 */
export function holdClickIdsFromUrl(): boolean {
  pending = readClickIdsFromUrl();
  return Object.keys(pending).length > 0;
}

/** Persist the held ids. Call only after the visitor allows it. */
export function commitHeldClickIds(): void {
  persist(pending);
  pending = {};
}

/** Drop the held ids without storing them. */
export function discardHeldClickIds(): void {
  pending = {};
}

export function readAttributionConsent(): AttributionConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw === "granted" || raw === "denied" ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Record the decision so the visitor is asked once, not on every page load.
 * Storing a consent choice is itself strictly necessary to honor that choice,
 * which is why this write does not need consent of its own.
 */
export function writeAttributionConsent(value: AttributionConsent): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // Private-mode / disabled storage — the visitor is asked again next load.
  }
}

/**
 * The click ids cleared for use, or undefined when there are none. Reads only
 * persisted ids, so an id held pending consent is never returned and therefore
 * never leaves the browser.
 */
export function getClickIds(): ClickIds | undefined {
  const stored = readStored();
  return Object.keys(stored).length > 0 ? stored : undefined;
}
